import React, { useCallback, useEffect, useState } from "react";
import { Radio, Users, X, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { getInitials, avatarColor } from "@/lib/avatar";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

// Go-Live button + a horizontal row of currently-live broadcasts for a module.
export default function LiveBar({ module, productId, title, showGoLive = true }) {
  const { t } = useI18n();
  const [lives, setLives] = useState([]);
  const [setup, setSetup] = useState(false);
  const [liveTitle, setLiveTitle] = useState(title || "");
  const [maxViewers, setMaxViewers] = useState("");
  const [when, setWhen] = useState("");

  const load = useCallback(() => {
    api.get("/live/active", { params: { module } }).then(({ data }) => setLives(data)).catch(() => {});
  }, [module]);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  useEffect(() => { const on = () => load(); window.addEventListener("rc-live-now", on); return () => window.removeEventListener("rc-live-now", on); }, [load]);

  const startLive = () => {
    const mv = parseInt(maxViewers, 10);
    window.dispatchEvent(new CustomEvent("rc-live-start", { detail: { module, product_id: productId, title: liveTitle.trim(), max_viewers: Number.isFinite(mv) && mv > 0 ? mv : 0 } }));
    setSetup(false);
  };
  const scheduleLive = async () => {
    if (!when) return toast.error(t("live_when_required"));
    try {
      await api.post("/live/schedule", { module, title: liveTitle.trim(), scheduled_at: new Date(when).toISOString() });
      toast.success(t("live_scheduled_ok"));
      setSetup(false); setWhen(""); setLiveTitle(title || "");
      window.dispatchEvent(new CustomEvent("rc-scheduled-live"));
    } catch { toast.error(t("s_failed")); }
  };
  const watch = (s) => window.dispatchEvent(new CustomEvent("rc-live-join", { detail: { session: s } }));

  return (
    <div className="mb-4" data-testid={`live-bar-${module}`}>
      <div className="flex items-center gap-3 overflow-x-auto no-scrollbar">
        {showGoLive && (
          <div data-testid={`go-live-${module}`} title={t("coming_soon")} className="relative shrink-0 flex flex-col items-center gap-1.5 opacity-60 cursor-not-allowed select-none">
            <span className="h-16 w-16 grid place-items-center rounded-full border-2 border-dashed border-red-500 text-red-500"><Radio size={22} /></span>
            <span data-testid={`go-live-soon-${module}`} className="absolute -top-1 left-1/2 -translate-x-1/2 rotate-6 whitespace-nowrap rounded-full bg-[var(--rc-gold)] text-black text-[9px] font-extrabold px-1.5 py-0.5 shadow">{t("coming_soon")}</span>
            <span className="text-[11px] text-[var(--rc-muted)]">{t("go_live")}</span>
          </div>
        )}
        {lives.map((s) => (
          <button key={s.id} onClick={() => watch(s)} data-testid={`live-tile-${s.id}`} className="shrink-0 flex flex-col items-center gap-1.5 w-16">
            <span className="relative h-16 w-16 rounded-full grid place-items-center text-[var(--rc-gold)] font-semibold overflow-hidden border-2 border-red-500" style={{ backgroundColor: s.host_avatar ? "var(--rc-bg)" : avatarColor(s.host_name) }}>
              {s.host_avatar ? <img src={s.host_avatar} alt="" className="h-full w-full object-cover" /> : getInitials(s.host_name)}
              <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 rounded-full bg-red-600 px-1.5 text-[9px] font-bold text-white">{t("live_badge")}</span>
            </span>
            <span className="flex items-center gap-0.5 text-[11px] text-[var(--rc-muted)] truncate max-w-full"><Users size={10} /> {s.viewer_count}</span>
          </button>
        ))}
      </div>

      {setup && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60" onClick={() => setSetup(false)} data-testid="live-setup">
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-4 space-y-3">
            <div className="flex items-center justify-between"><div className="font-semibold flex items-center gap-2 text-red-500"><Radio size={18} /> {t("go_live")}</div><button onClick={() => setSetup(false)}><X size={18} /></button></div>
            <div>
              <label className="block text-xs text-[var(--rc-muted)] mb-1">{t("live_title_label")}</label>
              <input value={liveTitle} onChange={(e) => setLiveTitle(e.target.value)} placeholder={t("live_title_ph")} data-testid="live-title-input"
                className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
            </div>
            <div>
              <label className="block text-xs text-[var(--rc-muted)] mb-1">{t("live_max_label")}</label>
              <input value={maxViewers} onChange={(e) => setMaxViewers(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder={t("live_max_ph")} data-testid="live-max-input"
                className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
            </div>
            <button onClick={startLive} data-testid="live-start-confirm" className="w-full rounded-xl bg-red-600 text-white font-semibold py-3 flex items-center justify-center gap-2"><Radio size={17} /> {t("go_live")}</button>

            <div className="pt-3 mt-1 border-t border-[var(--rc-border)]">
              <label className="block text-xs text-[var(--rc-muted)] mb-1">{t("live_when_label")}</label>
              <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="live-schedule-when"
                className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
              <button onClick={scheduleLive} data-testid="live-schedule-confirm" className="mt-2 w-full rounded-xl border border-[var(--rc-gold)]/50 text-[var(--rc-gold)] font-semibold py-2.5 flex items-center justify-center gap-2"><CalendarClock size={16} /> {t("live_schedule_for_later")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
